import { EPageTitles, EPublisherSolutionModel } from 'common/contracts';
import { EPermissionGroup } from 'common/permissions.types';
import { ELocalStorageKeys } from 'constants/enums';
import { EOrdersState } from 'constants/navigation';
import { localStorageUtil } from 'utils/localStorageUtil';
import { EPricingFormState } from 'views/Pricing/PricingView';
import { EReportsTabs } from 'views/Reports/types';

import { ESettingsState } from 'components/Settings/Settings.types';
import { EStoreState } from 'components/Theme/Theme';

import { EFeatureFlag } from '../../constants/enums';

import {
  ESideBarChipConfig,
  ESideBarChipType,
  ESidebarGroupKey,
  SideBarGroup,
  SideBarItem
} from './types';
import { ESideBarContext } from './types';

const isCheckoutUser =
  localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
  EPublisherSolutionModel.CHECKOUT;
// =====================
// Individual page definitions
// =====================

// Company-level pages
export const Orders: SideBarItem = {
  title: 'Orders',
  url: `orders`,
  icon: 'fa-credit-card',
  permissions: [EPermissionGroup.ORDERS],
  defaultTab: {
    [ESideBarContext.COMPANY]: EOrdersState.ORDERS
  }
};

export const Reports: SideBarItem = {
  title: 'Reports',
  url: `reports/financialreports`,
  icon: 'fa-file-chart-column',
  permissions: [EPermissionGroup.FINANCIAL_REPORT],
  defaultTab: {
    [ESideBarContext.COMPANY]: EReportsTabs.FINANCIAL
  }
};

export const Subscriptions: SideBarItem = {
  title: EPageTitles.SUBSCRIPTIONS,
  url: 'subscriptions',
  icon: 'fa-right-left',
  permissions: [EPermissionGroup.SUBSCRIPTIONS]
};

// Project-level pages - Management
export const Analytics: SideBarItem = {
  title: 'Analytics',
  url: 'statistics',
  icon: 'fa-regular fa-chart-simple',
  permissions: [EPermissionGroup.ANALYTICS]
};

export const Segments: SideBarItem = {
  title: 'Segments',
  url: 'segments',
  icon: 'fa-circle-quarter-stroke',
  permissions: [EPermissionGroup.SEGMENTS]
};

export const Pricing: SideBarItem = {
  title: 'Pricing',
  url: 'pricing/points',
  icon: 'fa-circle-dollar',
  permissions: [EPermissionGroup.PRICING],
  defaultTab: {
    [ESideBarContext.PROJECT]: EPricingFormState.PRICING_POINTS,
    [ESideBarContext.PORTAL_LEVEL_STORE]: EPricingFormState.PRICING_POINTS
  }
};

export const Coupons: SideBarItem = {
  title: EPageTitles.COUPONS,
  url: 'coupons',
  icon: 'fa-ticket-simple',
  permissions: [EPermissionGroup.COUPONS]
};
export const Localization: SideBarItem = {
  title: 'Translations',
  url: 'localization',
  icon: 'fa-globe',
  permissions: [EPermissionGroup.OFFERS]
};

export const Settings: SideBarItem = {
  title: 'Settings',
  url: 'settings',
  icon: 'fa-gear',
  permissions: [EPermissionGroup.COMPANY],
  defaultTab: {
    [ESideBarContext.PROJECT]: ESettingsState.DEVELOPER_SETTINGS,
    [ESideBarContext.PORTAL]: ESettingsState.DEVELOPER_SETTINGS,
    [ESideBarContext.PORTAL_LEVEL_STORE]: ESettingsState.DEVELOPER_SETTINGS
  }
};

export const PackageExplorer: SideBarItem = {
  title: EPageTitles.PACKAGE_EXPLORER,
  url: 'package-explorer',
  icon: 'fa-globe',
  permissions: [EPermissionGroup.COMPANY]
};

export const TestAndRelease: SideBarItem = {
  title: EPageTitles.TEST_AND_RELEASE,
  url: 'test-and-release',
  icon: 'fa-wrench',
  permissions: [EPermissionGroup.COMPANY]
};

// Project-level pages - Studio
export const Builder: SideBarItem = {
  title: 'Builder',
  url: 'theme',
  icon: 'fa-swatchbook',
  permissions: [EPermissionGroup.BUILDER],
  defaultTab: {
    [ESideBarContext.PROJECT]: isCheckoutUser
      ? EStoreState.CHECKOUT
      : EStoreState.GENERAL,
    [ESideBarContext.PORTAL_LEVEL_STORE]: EStoreState.GENERAL
  }
};
// Project-level pages - Studio
export const TemplatesStudio: SideBarItem = {
  title: EPageTitles.TEMPLATES_STUDIO,
  url: 'templates-studio',
  icon: 'fa-frame',
  permissions: [EPermissionGroup.BUILDER]
};

export const Products: SideBarItem = {
  title: 'Products',
  url: 'products',
  icon: 'fa-tag',
  permissions: [EPermissionGroup.PRODUCTS]
};

export const OffersDesign: SideBarItem = {
  title: 'Offers Design',
  url: 'offersui',
  icon: 'fa-palette',
  permissions: [EPermissionGroup.OFFER_DESIGN]
};

export const OfferDesignV2: SideBarItem = {
  title: EPageTitles.OFFER_DESIGN_V2,
  url: 'offerdesign',
  icon: 'fa-palette',
  permissions: [EPermissionGroup.OFFER_DESIGN]
};

export const CheckoutDesign: SideBarItem = {
  title: EPageTitles.CHECKOUT_DESIGN,
  url: 'checkoutdesigns',
  icon: 'fa-money-check-dollar-pen',
  permissions: [EPermissionGroup.BUILDER]
};

export const Badges: SideBarItem = {
  title: 'Badges',
  url: 'badges',
  icon: 'fa-badge-check',
  permissions: [EPermissionGroup.BADGES]
};

export const AssetsLibrary: SideBarItem = {
  title: 'Assets Library',
  url: 'assets',
  icon: 'fa-image',
  permissions: [EPermissionGroup.ASSET_LIBRARY]
};

// Project-level pages - Offers
export const Bundles: SideBarItem = {
  title: 'Bundles',
  url: 'bundles',
  icon: 'fa-bag-shopping',
  permissions: [EPermissionGroup.OFFERS]
};

export const Promotions: SideBarItem = {
  title: 'Promotions',
  url: 'promotions',
  icon: 'fa-gallery-thumbnails',
  permissions: [EPermissionGroup.OFFERS]
};

export const Popups: SideBarItem = {
  title: 'Popups',
  url: 'popups',
  icon: 'fa-gift',
  permissions: [EPermissionGroup.OFFERS]
};

export const Rolling: SideBarItem = {
  title: EPageTitles.ROLLING,
  url: 'rolling',
  icon: 'fa-grid-2-plus',
  permissions: [EPermissionGroup.OFFERS],
  docsLink: 'https://developers.appcharge.com/docs/rolling-offers'
};

export const CheckoutLink: SideBarItem = {
  title: EPageTitles.CHECKOUT_LINKS,
  url: 'checkoutlinks',
  icon: 'fa-square-up-right',
  permissions: [EPermissionGroup.OFFERS],
  docsLink: 'https://developers.appcharge.com/docs/about-checkout-link-offers'
};

export const ProgressBar: SideBarItem = {
  title: EPageTitles.PROGRESS_BAR,
  url: 'progressbars',
  icon: 'fa-bars-progress',
  permissions: [EPermissionGroup.OFFERS]
};

export const Plans: SideBarItem = {
  title: EPageTitles.PLANS,
  url: 'plans',
  icon: 'fa-grid-2-plus',
  permissions: [EPermissionGroup.OFFERS]
};

// Portal-level pages
export const Pages: SideBarItem = {
  title: 'Pages',
  url: 'pages',
  icon: 'fa-page'
};

export const Content: SideBarItem = {
  title: 'Content',
  url: 'content',
  icon: 'fa-pen'
};

export const PortalBuilder: SideBarItem = {
  title: 'Builder',
  url: 'builder',
  icon: 'fa-edit'
};

//Shared pages
export const Players: SideBarItem = {
  title: 'Players',
  url: 'players/blocked',
  icon: 'fa-user',
  permissions: [EPermissionGroup.PLAYER]
};

export const Profile: SideBarItem = {
  title: 'Profile',
  url: 'profile',
  icon: 'fa-user-circle'
};

// =====================
// Navigation structures using the individual page constants
// =====================

export const companyLevelNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Dashboard,
    isCollapsable: true,
    items: [Orders, Subscriptions, Reports]
  }
];

export const projectLevelNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Management,
    items: [
      Analytics,
      Players,
      Segments,
      Pricing,
      Coupons,
      Localization,
      Settings
    ],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Sdk,
    items: [PackageExplorer, TestAndRelease],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Studio,
    items: [
      Builder,
      TemplatesStudio,
      Products,
      OffersDesign,
      OfferDesignV2,
      CheckoutDesign,
      Badges,
      AssetsLibrary
    ],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Offers,
    items: [
      Bundles,
      Promotions,
      Popups,
      Rolling,
      CheckoutLink,
      ProgressBar,
      Plans
    ],
    isCollapsable: true
  }
];

export const paymentLinkProjectNavItems = (
  hasSingleProject: boolean
): SideBarGroup[] => [
  {
    title: ESidebarGroupKey.Dashboard,
    items: hasSingleProject
      ? [
          Analytics,
          Orders,
          Subscriptions,
          Reports,
          Players,
          Pricing,
          Coupons,
          Settings
        ]
      : [Analytics, Players, Pricing, Coupons, Settings],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Studio,
    items: [Builder, AssetsLibrary],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Offers,
    items: [Plans],
    isCollapsable: true
  }
];

export const portalLevelNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Management,
    items: [Players, Localization, Settings],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.PortalStudio,
    items: [PortalBuilder, Pages, Content],
    isCollapsable: true
  }
];

export const portalLevelStoreNavItems: SideBarGroup[] = [
  {
    title: ESidebarGroupKey.Management,
    items: [Pricing, Settings],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Studio,
    items: [
      Builder,
      TemplatesStudio,
      Products,
      OffersDesign,
      OfferDesignV2,
      CheckoutDesign,
      Badges,
      AssetsLibrary
    ],
    isCollapsable: true
  },
  {
    title: ESidebarGroupKey.Offers,
    items: [Bundles, Promotions, Popups, Rolling, CheckoutLink, ProgressBar],
    isCollapsable: true
  }
];

export const pageFeatureFlagMap: Record<EPageTitles | string, EFeatureFlag> = {
  [EPageTitles.CHECKOUT_DESIGN]: EFeatureFlag.DASHBOARD_CHECKOUT_DESIGN,
  [EPageTitles.ROLLING]: EFeatureFlag.DASHBOARD_ROLLING_OFFER_FEATURE,
  [EPageTitles.PROGRESS_BAR]: EFeatureFlag.DASHBOARD_PROGRESS_BAR,
  [EPageTitles.COUPONS]: EFeatureFlag.DASHBOARD_COUPONS,
  [EPageTitles.PACKAGE_EXPLORER]: EFeatureFlag.DASHBOARD_SDK_AUTO_UPDATER_UI,
  [EPageTitles.TEST_AND_RELEASE]: EFeatureFlag.DASHBOARD_SDK_AUTO_UPDATER_UI,
  [EPageTitles.SUBSCRIPTIONS]: EFeatureFlag.DASHBOARD_SUBSCRIPTIONS,
  [EPageTitles.PLANS]: EFeatureFlag.DASHBOARD_SUBSCRIPTIONS,
  [EPageTitles.TEMPLATES_STUDIO]: EFeatureFlag.DASHBOARD_TEMPLATES_STUDIO,
  [EPageTitles.OFFER_DESIGN_V2]: EFeatureFlag.DASHBOARD_IS_OFFER_DESIGN_V2_ENABLED
};

export const superAdminAllowedPages: EPageTitles[] = [
  EPageTitles.TEMPLATES_STUDIO
];

export const projectTypeRestrictions = {
  independentCheckout: {
    disabledItems: {
      [ESidebarGroupKey.Management]: [Segments],
      [ESidebarGroupKey.Studio]: [
        Products,
        OffersDesign,
        CheckoutDesign,
        AssetsLibrary,
        Badges,
        TemplatesStudio
      ],
      [ESidebarGroupKey.Offers]: [Bundles, Promotions, Popups, Rolling, CheckoutLink]
    } as Record<ESidebarGroupKey, SideBarItem[]>
  }
};

export const sidebarChipConfig: Record<ESideBarChipType, ESideBarChipConfig> = {
  coming_soon: {
    styles:
      'border-zinc-100 bg-zinc-100 text-zinc-500 group-hover/sidebar-menu-btn:border-violet-50 group-hover/sidebar-menu-btn:bg-violet-50 group-hover/sidebar-menu-btn:text-indigo-950 group-focus/sidebar-menu-btn:border-violet-50 group-focus/sidebar-menu-btn:bg-violet-50 group-focus/sidebar-menu-btn:text-indigo-950',
    disabledStateStyles: 'border-violet-50 bg-violet-50 text-indigo-950',
    title: 'Coming Soon',
    tooltip: 'Contact your account manager for early access and more details'
  },
  new: {
    styles: 'border-indigo-400 bg-indigo-400 text-base-white',
    title: 'New'
  },
  locked: {
    styles: 'border-zinc-700 bg-zinc-700 text-zinc-200',
    title: 'Locked',
    tooltip:
      'This feature is currently locked. Contact your Appcharge representative to learn more or request access.'
  },
  beta: {
    styles:
      'border-transparent bg-[rgba(14,165,233,0.20)] text-blue-950 px-2 py-1.5',
    title: 'Beta',
    tooltip: "This feature is in beta. We'd love your feedback!"
  }
};
